import { existsSync } from "node:fs";
import { isAbsolute } from "node:path";
import { fileURLToPath } from "node:url";
import { Worker } from "node:worker_threads";
import { createLoaderDataUrl } from "./loader-hooks.ts";

export type EvaluateResult =
  | { ok: true; value: Record<string, unknown>; dependencies: string[] }
  | { ok: false; error: { message: string; stack: string } };

export interface Evaluator {
  /** Evaluate a module and return its exports plus the full transitive dependency list. */
  evaluate(absolutePath: string): Promise<EvaluateResult>;
  /** Clear cached results for every entry point that transitively depends on the given file(s). */
  invalidate(...absolutePaths: string[]): void;
  /** Clear the entire result cache and swap workers. */
  invalidateAll(): void;
  /** Return entry point paths that would be affected by invalidate(absolutePath). */
  getDependentsOf(absolutePath: string): string[];
  /** Terminate both workers and reject any pending evaluations. */
  dispose(): Promise<void>;
  [Symbol.asyncDispose](): Promise<void>;
}

/** Wire format for messages from the worker back to the main thread. */
type WorkerResult =
  | {
      type: "result";
      id: number;
      ok: true;
      value: Record<string, unknown>;
      dependencies: string[];
    }
  | {
      type: "result";
      id: number;
      ok: false;
      error: { message: string; stack: string };
      dependencies: string[];
    };

interface PendingEvaluation {
  id: number;
  absolutePath: string;
  resolve: (result: EvaluateResult) => void;
  reject: (error: Error) => void;
  /** Number of times this evaluation has been retried due to staleness detection. */
  retryCount: number;
}

/**
 * Resolves the worker entry point path.
 *
 * In production the compiled `.js` file exists in `dist/` — we prefer that.
 * During development (vitest runs against source), only the `.ts` file exists.
 * Node 22.18.0+ handles `.ts` natively via strip-types, so the fallback works
 * without any flags or loaders.
 */
function resolveWorkerPath(): string {
  const jsPath = fileURLToPath(new URL("./worker.js", import.meta.url));
  if (existsSync(jsPath)) {
    return jsPath;
  }
  return fileURLToPath(new URL("./worker.ts", import.meta.url));
}

/**
 * Boots a worker and returns a handle with a ready promise.
 *
 * The loader hook data URL is passed via `workerData` rather than having
 * the worker import it from a sibling module. This avoids a module
 * resolution mismatch: source files use `.ts` extensions (required by
 * Node strip-types) while compiled output uses `.js` (rewritten by tsc
 * via `rewriteRelativeImportExtensions`). Since the worker file would need
 * to import loader-hooks with the right extension for its context, passing
 * the data URL through workerData eliminates the problem entirely.
 */
function bootWorker(): { worker: Worker; ready: Promise<void> } {
  const workerPath = resolveWorkerPath();
  const loaderDataUrl = createLoaderDataUrl();
  const worker = new Worker(workerPath, {
    workerData: { loaderDataUrl },
  });
  const ready = new Promise<void>((resolve) => {
    const onMessage = (msg: { type: string }) => {
      if (msg.type === "ready") {
        worker.off("message", onMessage);
        resolve();
      }
    };
    worker.on("message", onMessage);
  });
  return { worker, ready };
}

/** Default timeout for a single evaluation in milliseconds. */
const DEFAULT_EVAL_TIMEOUT_MS = 30_000;

/** Maximum number of staleness retries before giving up with an error. */
const MAX_STALENESS_RETRIES = 3;

/** Creates an evaluator that runs modules in isolated worker threads with dependency tracking. */
export async function createEvaluator(): Promise<Evaluator> {
  const resultCache = new Map<string, EvaluateResult>();
  /**
   * Tracks in-flight evaluation promises by path. When `evaluate()` is called
   * for a path that's already queued or being evaluated, the same promise is
   * returned instead of queuing a duplicate. This prevents a second worker
   * evaluation from producing incomplete dependencies (the ESM resolve hook
   * only fires on cache misses — a second import on the same worker would
   * miss transitive deps that are already in the ESM cache).
   */
  const inflight = new Map<string, Promise<EvaluateResult>>();

  /**
   * Two dependency maps provide O(1) lookups in both directions:
   *
   * - `reverseDeps`: file path → set of entry points that depend on it.
   *   Consulted during `invalidate()` to find which cached results to clear.
   *
   * - `forwardDeps`: entry point → set of all its transitive dependencies.
   *   Needed to clean up stale reverse entries when an entry point is
   *   re-evaluated and its dependency tree has changed (e.g. an import
   *   was removed). Without this, removed dependencies would accumulate
   *   phantom reverse entries that never get cleaned up.
   */
  const reverseDeps = new Map<string, Set<string>>();
  const forwardDeps = new Map<string, Set<string>>();

  let nextId = 0;
  let currentEval: PendingEvaluation | null = null;
  let currentTimeout: ReturnType<typeof setTimeout> | null = null;
  const queue: PendingEvaluation[] = [];
  /**
   * Tracks files passed to `invalidate()` while an evaluation is in-flight.
   * When an evaluation completes, `handleResult()` checks whether any reported
   * dependency intersects this set. If so, the result is stale — it's discarded,
   * workers are swapped, and the evaluation is retried on a clean worker.
   *
   * This closes the "cold start" gap: if `evaluate("A")` is dispatched before
   * `invalidate("B")` is called, and A depends on B, the dependency graph
   * doesn't know about A→B yet. Without this set, the invalidation would be
   * silently missed.
   *
   * Cleared in `swapWorkers()` because the new primary has a clean ESM cache.
   */
  const invalidatedDuringEval = new Set<string>();
  let disposed = false;

  /**
   * Two workers are maintained at all times:
   *
   * - **primary**: handles all evaluate() calls. Accumulates Node's ESM
   *   module cache over time, making repeated evaluations fast (~1-3ms).
   *
   * - **shadow**: pre-booted but completely idle. Never evaluates anything,
   *   so its module cache stays empty. On invalidation, the primary is
   *   terminated (stale cache) and the shadow is promoted to primary
   *   instantly (no boot latency). A new shadow is booted in the background.
   *
   * This pattern trades ~30ms of background boot time for instant
   * invalidation. The alternative — reusing a single worker and clearing
   * its ESM cache — isn't possible because Node doesn't expose an API
   * to clear the module cache.
   */
  let primary = bootWorker();
  let shadow = bootWorker();

  await Promise.all([primary.ready, shadow.ready]);

  /**
   * Allow the process to exit naturally when the event loop is otherwise empty.
   * Without this, the workers keep the event loop alive indefinitely — causing
   * hangs in multi-environment Vite builds where no hook reliably disposes
   * the evaluator after all environments are done.
   * Must be called after setupMessageHandler since .on() can re-ref the worker.
   */
  function unrefWorkers(primaryWorker: Worker, shadowWorker: Worker) {
    primaryWorker.unref();
    shadowWorker.unref();
  }

  function setupMessageHandler(workerObj: { worker: Worker }) {
    workerObj.worker.on("message", (msg: WorkerResult) => {
      if (msg.type !== "result") return;
      handleResult(msg);
    });

    workerObj.worker.on("error", (err: Error) => {
      if (currentEval) {
        clearEvalTimeout();
        const pending = currentEval;
        currentEval = null;
        const result: EvaluateResult = {
          ok: false,
          error: { message: err.message, stack: err.stack ?? "" },
        };
        updateDeps(pending.absolutePath, [pending.absolutePath]);
        resultCache.set(pending.absolutePath, result);
        inflight.delete(pending.absolutePath);
        pending.resolve(result);
        processQueue();
      }
    });
  }

  setupMessageHandler(primary);
  unrefWorkers(primary.worker, shadow.worker);

  function clearEvalTimeout() {
    if (currentTimeout !== null) {
      clearTimeout(currentTimeout);
      currentTimeout = null;
    }
  }

  function handleResult(msg: WorkerResult) {
    if (!currentEval) return;
    if (msg.id !== currentEval.id) return;

    clearEvalTimeout();

    const pending = currentEval;
    currentEval = null;

    // Staleness check: if a file was invalidated while this evaluation was
    // in-flight and the result depends on it, discard the result and retry
    // on a clean worker. Only checked for successes — errors don't report
    // deps, and the file watcher will trigger another invalidation when the
    // source is fixed.
    if (msg.ok && invalidatedDuringEval.size > 0) {
      const isStale = msg.dependencies.some((dep) => invalidatedDuringEval.has(dep));
      if (isStale) {
        if (pending.retryCount >= MAX_STALENESS_RETRIES) {
          pending.resolve({
            ok: false,
            error: {
              message: `Evaluation of ${pending.absolutePath} exceeded maximum staleness retries (${MAX_STALENESS_RETRIES})`,
              stack: "",
            },
          });
          processQueue();
          return;
        }
        pending.retryCount++;
        swapWorkers();
        queue.unshift(pending);
        processQueue();
        return;
      }
    }

    let result: EvaluateResult;
    if (msg.ok) {
      result = { ok: true, value: msg.value, dependencies: msg.dependencies };
      updateDeps(pending.absolutePath, msg.dependencies);
    } else {
      result = { ok: false, error: msg.error };
      // Use the partial dependencies reported by the worker (files resolved
      // before the error). This ensures transitive deps are registered in
      // reverseDeps so that fixing a broken file triggers re-evaluation.
      updateDeps(pending.absolutePath, msg.dependencies);
    }

    resultCache.set(pending.absolutePath, result);
    inflight.delete(pending.absolutePath);
    pending.resolve(result);
    processQueue();
  }

  /**
   * Reconciles the dependency graph after an evaluation.
   *
   * First removes all old reverse entries for this entry point (using
   * forwardDeps as the source of truth), then writes the new forward
   * and reverse mappings. This ensures that if a dependency was removed
   * between evaluations, it no longer points back to this entry point.
   */
  function updateDeps(entryPoint: string, deps: string[]) {
    const oldDeps = forwardDeps.get(entryPoint);
    if (oldDeps) {
      for (const dep of oldDeps) {
        const entries = reverseDeps.get(dep);
        if (entries) {
          entries.delete(entryPoint);
          if (entries.size === 0) {
            reverseDeps.delete(dep);
          }
        }
      }
    }

    const depSet = new Set(deps);
    forwardDeps.set(entryPoint, depSet);

    for (const dep of depSet) {
      let entries = reverseDeps.get(dep);
      if (!entries) {
        entries = new Set();
        reverseDeps.set(dep, entries);
      }
      entries.add(entryPoint);
    }
  }

  /**
   * Evaluations are serialized — one at a time per worker. This prevents
   * interleaved dependency tracking: the loader hook accumulates deps in
   * a single Set and flushes it after each evaluation. Concurrent imports
   * would mix dependencies from different entry points.
   *
   * Waits for the primary worker to be ready before sending work. This
   * matters after a swap: the promoted shadow may still be booting.
   */
  function processQueue() {
    if (currentEval || queue.length === 0 || disposed) return;

    const next = queue.shift()!;

    // A prior evaluation for the same path may have completed and cached
    // while this item was queued (e.g. after invalidation + re-queue).
    // Resolving from cache avoids dispatching to a worker where the module
    // is already in the ESM cache (which would produce incomplete deps).
    const alreadyCached = resultCache.get(next.absolutePath);
    if (alreadyCached) {
      inflight.delete(next.absolutePath);
      next.resolve(alreadyCached);
      processQueue();
      return;
    }

    currentEval = next;

    primary.ready.then(() => {
      if (disposed || currentEval !== next) return;
      primary.worker.postMessage({
        type: "evaluate",
        id: next.id,
        absolutePath: next.absolutePath,
      });

      currentTimeout = setTimeout(() => {
        if (currentEval !== next) return;
        currentEval = null;
        currentTimeout = null;

        const result: EvaluateResult = {
          ok: false,
          error: {
            message: `Evaluation of ${next.absolutePath} timed out after ${DEFAULT_EVAL_TIMEOUT_MS}ms`,
            stack: "",
          },
        };
        updateDeps(next.absolutePath, [next.absolutePath]);
        resultCache.set(next.absolutePath, result);
        inflight.delete(next.absolutePath);
        next.resolve(result);

        swapWorkers();
        processQueue();
      }, DEFAULT_EVAL_TIMEOUT_MS);
    });
  }

  /**
   * Terminates the primary (stale module cache), promotes the shadow
   * (clean cache, instant), and boots a new shadow in the background.
   *
   * `removeAllListeners` prevents the terminated worker from delivering
   * late messages or error events that would confuse the result handler.
   */
  function swapWorkers() {
    primary.worker.removeAllListeners();
    primary.worker.terminate();

    primary = shadow;
    setupMessageHandler(primary);

    shadow = bootWorker();
    unrefWorkers(primary.worker, shadow.worker);

    // The new primary has a clean ESM cache, so any previously tracked
    // invalidations are no longer relevant — the fresh worker will
    // re-import everything from disk.
    invalidatedDuringEval.clear();
  }

  function evaluate(absolutePath: string): Promise<EvaluateResult> {
    if (disposed) {
      return Promise.reject(new Error("Evaluator has been disposed"));
    }

    if (!isAbsolute(absolutePath)) {
      return Promise.reject(
        new Error(`evaluate() requires an absolute path, got: ${absolutePath}`),
      );
    }

    const cached = resultCache.get(absolutePath);
    if (cached) {
      return Promise.resolve(cached);
    }

    // Deduplicate: if an evaluation is already queued or in-flight for this
    // path, return the same promise. This prevents a second worker evaluation
    // that would produce incomplete dependencies (the ESM resolve hook only
    // fires on cache misses).
    const pending = inflight.get(absolutePath);
    if (pending) return pending;

    const promise = new Promise<EvaluateResult>((resolve, reject) => {
      const id = nextId++;
      queue.push({ id, absolutePath, resolve, reject, retryCount: 0 });
      processQueue();
    });

    inflight.set(absolutePath, promise);
    return promise;
  }

  function invalidate(...absolutePaths: string[]): void {
    if (absolutePaths.length === 0) return;

    // Track all invalidations for cold-start detection (in-flight evaluations
    // whose dependency graph isn't populated yet).
    for (const path of absolutePaths) {
      invalidatedDuringEval.add(path);
    }

    // Collect all affected entry points across all changed files.
    const allEntryPoints = new Set<string>();
    for (const path of absolutePaths) {
      const entryPoints = reverseDeps.get(path);
      if (entryPoints) {
        for (const entry of entryPoints) {
          allEntryPoints.add(entry);
        }
      }
    }

    // If no cached entry points are affected, return early. The cold-start
    // set (invalidatedDuringEval) will catch in-flight evaluations.
    if (allEntryPoints.size === 0) return;

    // Clear caches and clean dependency graphs for all affected entry points.
    for (const entry of allEntryPoints) {
      resultCache.delete(entry);
      inflight.delete(entry);

      const deps = forwardDeps.get(entry);
      if (deps) {
        for (const dep of deps) {
          const entries = reverseDeps.get(dep);
          if (entries) {
            entries.delete(entry);
            if (entries.size === 0) {
              reverseDeps.delete(dep);
            }
          }
        }
        forwardDeps.delete(entry);
      }
    }

    // If an evaluation is in-flight on the worker we're about to terminate,
    // capture it and re-queue at the front. The caller's promise will
    // resolve transparently with the result from the promoted shadow worker.
    clearEvalTimeout();
    const inflightEval = currentEval;
    currentEval = null;

    swapWorkers();

    if (inflightEval) {
      queue.unshift(inflightEval);
    }

    processQueue();
  }

  function invalidateAll(): void {
    resultCache.clear();
    inflight.clear();
    forwardDeps.clear();
    reverseDeps.clear();

    clearEvalTimeout();
    const inflightEval = currentEval;
    currentEval = null;

    swapWorkers();

    if (inflightEval) {
      queue.unshift(inflightEval);
    }

    processQueue();
  }

  function getDependentsOf(absolutePath: string): string[] {
    const entryPoints = reverseDeps.get(absolutePath);
    if (!entryPoints) return [];
    return [...entryPoints];
  }

  async function dispose(): Promise<void> {
    if (disposed) return;
    disposed = true;

    clearEvalTimeout();

    if (currentEval) {
      currentEval.reject(new Error("Evaluator has been disposed"));
      currentEval = null;
    }
    for (const pending of queue) {
      pending.reject(new Error("Evaluator has been disposed"));
    }
    queue.length = 0;
    inflight.clear();

    await Promise.all([primary.worker.terminate(), shadow.worker.terminate()]);
  }

  return {
    evaluate,
    invalidate,
    invalidateAll,
    getDependentsOf,
    dispose,
    [Symbol.asyncDispose]: dispose,
  };
}
